import { SxProps, Theme } from '@mui/material';

import { tierBadgeClassNames } from '../../../components/academy/TierBadge/TierBadge.styles';
import { FONT_FAMILY, TEXT_DARK } from '../../../styles/GlobalStyles';

const ACADEMY_MAIN_WIDTH_PX = 720;
const ACADEMY_SIDE_WIDTH_PX = 360;
const ACADEMY_OVERVIEW_GAP_PX = 56;
const ACADEMY_OVERVIEW_WIDTH_PX =
  ACADEMY_MAIN_WIDTH_PX + ACADEMY_SIDE_WIDTH_PX + ACADEMY_OVERVIEW_GAP_PX;
const ACADEMY_SECTION_GAP_PX = 28;
const ACADEMY_TEXT = '#111827';
const ACADEMY_MUTED_TEXT = '#5f6670';

export const academyClassNames = {
  shell: 'academy-shell',
};

export const academyStyles = {
  shell: (theme: Theme, removeTopMargin = false) =>
    ({
      height: 'calc(100vh - 150px)',
      marginTop: removeTopMargin ? '0px' : '50px',
      overflowX: 'hidden',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      boxSizing: 'border-box',
      color: '#1f2933',
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightLight,
      fontSynthesis: 'none',
      textRendering: 'optimizeLegibility',
      background: 'transparent',
      '&, & *': {
        boxSizing: 'border-box',
      },
      '& button, & input, & textarea, & select': {
        font: 'inherit',
      },
      '& a': {
        color: 'inherit',
      },
      '& h1, & h2, & h3, & h4, & h5, & h6, & p': {
        margin: 0,
        fontWeight: 300,
      },
    }) as SxProps<Theme>,

  content: (theme: Theme) =>
    ({
      display: 'grid',
      gap: `${ACADEMY_SECTION_GAP_PX}px`,
      width: 'min(1260px, calc(100% - 32px))',
      margin: '0 auto',
      padding: `${theme.spacing(2)} 0 42px`,
      [theme.breakpoints.down('md')]: {
        paddingTop: theme.spacing(2),
      },
    }) as SxProps<Theme>,

  overview: (theme: Theme) =>
    ({
      display: 'grid',
      gridTemplateColumns: `minmax(0, ${ACADEMY_MAIN_WIDTH_PX}px) minmax(280px, ${ACADEMY_SIDE_WIDTH_PX}px)`,
      gridTemplateAreas: "'hero metrics' 'tiers tiers'",
      columnGap: `${ACADEMY_OVERVIEW_GAP_PX}px`,
      rowGap: `${ACADEMY_SECTION_GAP_PX}px`,
      alignItems: 'start',
      justifySelf: 'center',
      width: `min(100%, ${ACADEMY_OVERVIEW_WIDTH_PX}px)`,
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(240px, 320px)',
        columnGap: '24px',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: "'hero' 'tiers' 'metrics'",
        width: '100%',
      },
    }) as SxProps<Theme>,

  heroCopy: (theme: Theme) =>
    ({
      gridArea: 'hero',
      display: 'grid',
      gap: theme.spacing(1),
    }) as SxProps<Theme>,

  titleRow: () =>
    ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: 0,
    }) as SxProps<Theme>,

  title: () =>
    ({
      color: '#47505a',
      fontSize: '25px',
      fontWeight: 500,
      lineHeight: '25px',
      margin: 0,
      letterSpacing: 0,
    }) as SxProps<Theme>,

  description: () =>
    ({
      color: '#5d6670',
      fontSize: '16px',
      fontWeight: 300,
      lineHeight: 1.35,
      margin: 0,
      letterSpacing: 0,
    }) as SxProps<Theme>,

  metricGrid: (theme: Theme) =>
    ({
      gridArea: 'metrics',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '8px',
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    }) as SxProps<Theme>,

  metricItem: () =>
    ({
      minWidth: 0,
      borderRadius: '8px',
      padding: '14px 10px',
      backgroundColor: '#ffffff',
      boxShadow: '0 6px 14px rgb(39 53 71 / 16%)',
      textAlign: 'center',
    }) as SxProps<Theme>,

  metricLabel: () =>
    ({
      color: ACADEMY_MUTED_TEXT,
      fontSize: '14px',
      lineHeight: 1.15,
      margin: 0,
    }) as SxProps<Theme>,

  metricValue: () =>
    ({
      margin: '0 0 4px',
      color: '#4d4f53',
      fontSize: '28px',
      fontWeight: 800,
      lineHeight: 1,
    }) as SxProps<Theme>,

  tierSection: () =>
    ({
      gridArea: 'tiers',
      display: 'grid',
      justifySelf: 'stretch',
      width: '100%',
      gap: '12px',
    }) as SxProps<Theme>,

  section: () =>
    ({
      display: 'grid',
      justifySelf: 'center',
      gap: '12px',
      width: `min(100%, ${ACADEMY_OVERVIEW_WIDTH_PX}px)`,
    }) as SxProps<Theme>,

  welcomeAlert: (theme: Theme) =>
    ({
      position: 'relative',
      width: '86%',
      backgroundColor: theme.palette.success.light,
      color: TEXT_DARK,
      fontFamily: FONT_FAMILY,
      fontSize: '16px',
      fontWeight: 300,
      lineHeight: 1.428571429,
      letterSpacing: 'normal',
      textAlign: 'center',
      '& .MuiAlert-message': {
        width: '100%',
        textAlign: 'center',
      },
      '& .MuiLink-root': {
        color: theme.palette.primary.main,
      },
      '& .MuiAlert-action': {
        position: 'absolute',
        top: '50%',
        right: theme.spacing(1),
        transform: 'translateY(-50%)',
        margin: 0,
        padding: 0,
        color: theme.palette.success.main,
      },
    }) as SxProps<Theme>,

  sectionTitle: () =>
    ({
      color: '#4d535a',
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: 1.2,
      margin: 0,
      letterSpacing: 0,
    }) as SxProps<Theme>,

  tierCard: (theme: Theme, isStacked: boolean) =>
    ({
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: isStacked ? '1fr' : 'repeat(2, minmax(0, 1fr))',
      gap: '8px',
      width: '100%',
      overflow: 'visible',
      borderRadius: 0,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    }) as SxProps<Theme>,

  tierItem: (theme: Theme, isAvailable: boolean) =>
    ({
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      alignContent: 'start',
      alignItems: 'center',
      justifyItems: 'start',
      gap: '14px',
      minHeight: '60px',
      border: 0,
      borderRadius: '8px',
      overflow: 'hidden',
      padding: 0,
      backgroundColor: isAvailable ? '#ffffff' : '#f5f5f5',
      boxShadow: '0 8px 20px rgb(39 53 71 / 18%)',
      color: isAvailable ? ACADEMY_TEXT : '#6e7277',
      textAlign: 'left',
      textTransform: 'none',
      cursor: isAvailable ? 'pointer' : 'not-allowed',
      '&.MuiButton-root.Mui-disabled': {
        color: '#6e7277',
      },
      '&:focus-visible': {
        zIndex: 2,
        outline: `3px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
      [`& .${tierBadgeClassNames.root}`]: {
        marginLeft: '10px',
      },
    }) as SxProps<Theme>,

  tierCopy: () =>
    ({
      minWidth: 0,
      padding: '5px 0',
    }) as SxProps<Theme>,

  tierHeadingRow: () =>
    ({
      display: 'grid',
      alignItems: 'start',
      gap: 0,
      minWidth: 0,
      whiteSpace: 'nowrap',
      '& > *': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }) as SxProps<Theme>,

  tierHeading: () =>
    ({
      color: '#454b52',
      fontSize: '20px',
      fontWeight: 300,
      lineHeight: 1.15,
      margin: 0,
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>,

  tierInlineDescription: () =>
    ({
      color: ACADEMY_MUTED_TEXT,
      fontSize: '14px',
      fontStyle: 'italic',
      fontWeight: 300,
      lineHeight: 1.2,
      margin: 0,
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>,

  tierProgress: (theme: Theme, isComplete = false) =>
    ({
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '12px',
      overflow: 'hidden',
      borderRadius: '0 0 8px 8px',
      backgroundColor: '#ffffff',
      pointerEvents: 'none',
      '& .MuiLinearProgress-bar': {
        position: 'relative',
        display: 'block',
        minWidth: 0,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.primary.main,
        clipPath: isComplete ? 'none' : 'polygon(0 0, 100% 0, calc(100% - 6px) 100%, 0 100%)',
      },
    }) as SxProps<Theme>,

  cardGrid: (theme: Theme) =>
    ({
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '24px',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
      },
    }) as SxProps<Theme>,

  lessonCard: () =>
    ({
      display: 'grid',
      gridTemplateRows: 'auto auto auto',
      minWidth: 0,
      overflow: 'hidden',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      boxShadow: '0 8px 20px rgb(39 53 71 / 18%)',
    }) as SxProps<Theme>,

  pathCard: () =>
    ({
      display: 'grid',
      gridTemplateRows: 'auto 1fr',
      minWidth: 0,
      overflow: 'hidden',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      boxShadow: '0 8px 20px rgb(39 53 71 / 18%)',
    }) as SxProps<Theme>,

  lessonHeading: () =>
    ({
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: '16px',
      alignItems: 'center',
      padding: '20px 24px 10px',
    }) as SxProps<Theme>,

  cardTitle: () =>
    ({
      color: ACADEMY_TEXT,
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: 1.18,
      margin: 0,
      letterSpacing: 0,
    }) as SxProps<Theme>,

  lessonDescription: () =>
    ({
      padding: '0 24px 18px',
      color: ACADEMY_TEXT,
      fontSize: '16px',
      fontWeight: 300,
      lineHeight: 1.25,
      margin: 0,
    }) as SxProps<Theme>,

  lessonActions: () =>
    ({
      display: 'flex',
      flexWrap: 'wrap',
      gap: '14px',
      alignItems: 'center',
      borderTop: '1px solid #edf0f2',
      padding: '12px 20px',
    }) as SxProps<Theme>,

  lessonActionButton: (theme: Theme) =>
    ({
      display: 'inline-grid',
      placeItems: 'center',
      minWidth: '88px',
      minHeight: '34px',
      padding: theme.spacing(0.5, 1.5),
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '7px',
      backgroundColor: '#ffffff',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      fontSize: '18px',
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: 1.2,
      textDecoration: 'none',
      textTransform: 'none',
      '&&': {
        fontSize: '18px',
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: 1.2,
      },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
    }) as SxProps<Theme>,

  pathHeading: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      minHeight: '64px',
      padding: '14px 20px',
      backgroundColor: '#d8eefc',
      color: theme.palette.primary.main,
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: 1.18,
      margin: 0,
      letterSpacing: 0,
    }) as SxProps<Theme>,

  pathIcon: (theme: Theme, large = false) =>
    ({
      display: 'grid',
      placeItems: 'center',
      width: large ? '48px' : '36px',
      height: large ? '48px' : '36px',
      flex: '0 0 auto',
      borderRadius: '999px',
      backgroundColor: large ? '#d8eefc' : '#ffffff',
      color: theme.palette.primary.main,
      padding: large ? '9px' : '7px',
    }) as SxProps<Theme>,

  pathIconImage: () =>
    ({
      display: 'block',
      width: '100%',
      height: '100%',
      backgroundColor: 'currentColor',
      mask: 'var(--skill-icon-url) center / contain no-repeat',
      WebkitMask: 'var(--skill-icon-url) center / contain no-repeat',
    }) as SxProps<Theme>,

  pathSkillList: () =>
    ({
      display: 'grid',
      gridAutoRows: 'minmax(42px, auto)',
      alignContent: 'start',
      gap: 0,
      margin: 0,
      padding: '8px 20px 7px',
      listStyle: 'none',
    }) as SxProps<Theme>,

  pathSkillListItem: () =>
    ({
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: '10px',
      alignItems: 'center',
      color: ACADEMY_TEXT,
      fontSize: '16px',
      fontWeight: 300,
      lineHeight: 1.25,
    }) as SxProps<Theme>,

  skillPathButton: (theme: Theme, isSelected: boolean) =>
    ({
      minWidth: 0,
      justifyContent: 'flex-start',
      border: 0,
      padding: 0,
      backgroundColor: 'transparent',
      color: isSelected ? theme.palette.primary.main : 'inherit',
      fontWeight: isSelected ? 700 : 300,
      lineHeight: 1.25,
      textAlign: 'left',
      textTransform: 'none',
      cursor: 'pointer',
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
    }) as SxProps<Theme>,

  statePanel: (isError = false) =>
    ({
      width: 'min(640px, calc(100% - 32px))',
      margin: '80px auto 0',
      borderRadius: '8px',
      padding: '20px',
      backgroundColor: '#ffffff',
      boxShadow: '0 8px 20px rgb(39 53 71 / 18%)',
      color: isError ? '#7a271a' : 'inherit',
      fontSize: '16px',
      lineHeight: 1.35,
    }) as SxProps<Theme>,

  emptyState: () =>
    ({
      display: 'grid',
      gap: '10px',
      width: 'min(640px, calc(100% - 32px))',
      margin: '80px auto 0',
      borderRadius: '8px',
      padding: '28px',
      backgroundColor: '#ffffff',
      boxShadow: '0 8px 20px rgb(39 53 71 / 18%)',
    }) as SxProps<Theme>,

  emptyTitle: () =>
    ({
      color: '#47505a',
      fontSize: '25px',
      fontWeight: 500,
      lineHeight: '25px',
      margin: 0,
      letterSpacing: 0,
    }) as SxProps<Theme>,

  emptyText: () =>
    ({
      color: '#5d6670',
      fontSize: '16px',
      fontWeight: 300,
      lineHeight: 1.35,
      margin: 0,
    }) as SxProps<Theme>,
};
